import React, { useState } from "react";
import { Building2, Landmark, User, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";

/** Herkunft einer unbekannten Karte: eigener Bankauszug, Schwesterfirma (Karte/Bank/Betrag) oder Privatkarte dort. */
export const CardOriginHint = ({ companyId, item, onDone }) => {
  const o = item.card_origin;
  const pc = item.extracted?.payment_card || {};
  const [busy, setBusy] = useState(false);
  if (!o) return null;

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { const r = await fn(); toast.success(typeof okMsg === "function" ? okMsg(r) : okMsg); onDone?.(r); }
    catch (e) { toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen"); }
    finally { setBusy(false); }
  };
  const registerOwn = () => run(async () => {
    const r = await apiClient.post(`/companies/${companyId}/cards`, { last4: pc.last4, brand: pc.brand || null, kind: "company", label: `Firmenkarte …${pc.last4}` });
    return r.data;
  }, (d) => `Firmenkarte …${pc.last4} gespeichert · auf ${d?.applied_to_pending || 0} offene Belege angewendet`);
  const moveToCompany = () => run(async () => {
    if (o.kind !== "other_card") {
      await apiClient.post(`/companies/${o.company_id}/cards`, { last4: pc.last4, brand: pc.brand || null, kind: "company", label: `Firmenkarte …${pc.last4}` }).catch(() => {});
    }
    const r = await apiClient.post(`/companies/${companyId}/inbox/${item.id}/move-company`, { target_company_id: o.company_id });
    return r.data;
  }, o.is_private ? `Privater Beleg → «${o.company_name}» verschoben – Karte …${pc.last4} dort gemerkt` : `Beleg zu «${o.company_name}» verschoben – Karte …${pc.last4} dort als Firmenkarte gemerkt`);
  const asPrivate = () => run(async () => {
    const r = await apiClient.post(`/companies/${companyId}/inbox/${item.id}/assign-card`, { kind: "employee_private", employee_id: o.employee_id || null, label: o.holder_name || null });
    return r.data;
  }, `Als Spesen von ${o.holder_name || "Mitarbeiter"} erfasst`);

  const Icon = o.kind === "own_bank" ? Landmark : o.kind === "other_private" ? User : Building2;
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-[11px] text-amber-950 space-y-1.5" data-testid={`card-origin-${item.id}`}>
      <div className="flex items-start gap-1.5">
        <Icon className="h-3.5 w-3.5 shrink-0 mt-px" />
        <span><strong>Achtung – Herkunft erkannt:</strong> {o.reason}</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {o.kind === "own_bank" && (
          <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={registerOwn} data-testid={`card-origin-register-${item.id}`}>
            {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />} Als Firmenkarte übernehmen
          </Button>
        )}
        {(o.kind === "other_card" || o.kind === "other_bank" || o.kind === "other_amount") && (
          <Button size="sm" className="h-7 bg-indigo-600 hover:bg-indigo-700" disabled={busy} onClick={moveToCompany} data-testid={`card-origin-move-${item.id}`}>
            {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : o.is_private ? <User className="h-3 w-3 mr-1" /> : <Building2 className="h-3 w-3 mr-1" />} {o.is_private ? `Bestätigen: privater Beleg → «${o.company_name}»` : `Bestätigen: Beleg gehört zu «${o.company_name}»`}
          </Button>
        )}
        {o.kind === "other_private" && (
          <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={asPrivate} data-testid={`card-origin-private-${item.id}`}>
            {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <User className="h-3 w-3 mr-1" />} Bestätigen: privat bezahlt von {o.holder_name || "Mitarbeiter"} (Spesen)
          </Button>
        )}
      </div>
    </div>
  );
};
